
const $ = window.jQuery;
import plugin, { PluginBase } from '../utils/plugin';
import imagesLoaded from 'imagesloaded';
import 'intersection-observer';
import { decode as decodeBlurhash, isBlurhashValid } from "blurhash";

class PluginClass extends PluginBase {

	constructor (el, options) {

    super( el, options );

    this.$el = $(el);
    this.options = options;
    this.isVisible = false;
    this.fullSrc = this.$el.data('sources').full[0];

    let iosVersion = this.getIOSVersion();
    if( iosVersion && iosVersion.Full_Release_Numeric < 12.2 ) {
      this.log('IOS < 12.2 detected, deactivating lazyloading');
      this.options.lazy = false;
    };

    window.lazyLoad = window.lazyLoad || {};
    window.lazyLoad.observers = window.lazyLoad.observers || {};

    this.addEvent($(window), 'resize');
    this.io = this.getIntersectionObserver();

    this.$el = this.$el.filter('[data-sources],[data-src]');
    if( !this.$el.length ) {
      this.log('element doesn\'t support lazyloading', this.fullSrc);
    }
    if( this.options.lazy ) {
      this.$el.on('visible.lazyload', () => {
        this.log( 'became visible:', this.fullSrc );
        this.isVisible = true;
        this.load();
      });
      this.$el.on('hidden.lazyload', () => {
        this.isVisible = false;
      });
      this.io.observe(el);
    } else {
      this.load();
    }

    return this;

	}

  onresize() {
    if( this.isVisible && this.$el.hasClass('lazyloaded') ) this.load();
  }

  destroy() {
    super.destroy();
    if(this.io) {
      this.io.disconnect();
    }
  }

  getIOSVersion() {
    if(navigator.userAgent.match(/ipad|iphone|ipod/i)){ //if the current device is an iDevice
      var ios_info ={};
      ios_info.User_Agent=navigator.userAgent;
      ios_info.As_Reported=(navigator.userAgent).match(/OS (\d)?\d_\d(_\d)?/i)[0];
      ios_info.Major_Release=(navigator.userAgent).match(/OS (\d)?\d_\d(_\d)?/i)[0].split('_')[0];
      ios_info.Full_Release=(navigator.userAgent).match(/OS (\d)?\d_\d(_\d)?/i)[0].replace(/_/g,".");
      ios_info.Major_Release_Numeric=+(navigator.userAgent).match(/OS (\d)?\d_\d(_\d)?/i)[0].split('_')[0].replace("OS ","");
      ios_info.Full_Release_Numeric=+(navigator.userAgent).match(/OS (\d)?\d_\d(_\d)?/i)[0].replace("_",".").replace("_","").replace("OS ","");   //converts versions like 4.3.3 to numeric value 4.33 for ease of numeric comparisons
      return(ios_info);
    }
    return false;
  }

  getIntersectionObserver() {
    let observer = window.lazyLoad.observers.intersection
    if( typeof observer !== 'undefined' ) return observer
    observer = new IntersectionObserver(items => {
      items.forEach((item, i) => {
        let $el = $(item.target);
        if(item.isIntersecting) {
          $el.trigger('visible.lazyload');
        } else {
          $el.trigger('hidden.lazyload');
        }
      });
    }, {
      rootMargin: '1000px',
      trackVisibility: true,
      delay: 100
    });
    window.lazyLoad.observers.intersection = observer
    return observer
  }

  onLoad( response ) {
    if( this.maybeDestroy() ) {
      return;
    }

    this.$el.removeClass('lazyloading');
    this.$el.parents('.image:first').addClass('lazyloaded');
    this.$el.trigger('lazyloaded');
    this.options.onLoad( this.$el, this, response );
  }

  load() {
    this.log( 'loading:', this.fullSrc );
    this.renderBlurhash();
    this.$el.addClass('lazyloading');
    let tagName = this.$el.prop('tagName').toLowerCase();
    switch( tagName ) {
      case 'img':
      this.loadImage( this.$el ).then(response => this.onLoad(response))
      break;
      case 'image':

      this.loadSvgImage( this.$el ).then(response => this.onLoad(response));
      break;

      case 'iframe':
      this.loadIframe( this.$el ).then(response => this.onLoad(response));
      break;

      default:
      this.loadBackgroundImage( this.$el ).then(response => this.onLoad(response));
      break;
    }
  }

  /*
  * get the size of the image depending on it's ratio and the parent's size
  */
  getScaledImageWidth( $img ) {
    let imgRatio = $img.data('ratio');
    if( !imgRatio ) {
      this.log('no ratio defined on image, falling back to 1', $img[0]);
      imgRatio = 1;
    }
    let $wrap = $img.parents('.scaled-media:first');
    if( !$wrap.length ) {
      this.log('no wrapper found!', '.scaled-media:first');
      return 0;
    }
    let fitting = $wrap.data('fitting');
    let wrapWidth = $wrap.width();
    let wrapHeight = $wrap.height();
    let width = 0;

    switch( fitting ) {
      // contain
      case 'contain':
      width = Math.min( wrapWidth, wrapHeight * imgRatio );
      break;
      // cover
      default:
      width = Math.max( wrapWidth, wrapHeight * imgRatio );
      break;
    }
    width *= 1.2; // retina support

    return width;
  }

  loadImage( $img ) {

    let imgWidth = this.getScaledImageWidth( $img );

    let src = $img.attr('src');
    let selectedSrc = this.selectSource( $img, imgWidth );

    return new Promise( (resolve, reject) => {
      if( src === selectedSrc ) {
        resolve('image doesn\'t need loading');
      } else {
        $img.attr('src', selectedSrc);
        imagesLoaded($img[0], () => {
          $img.addClass('lazyloaded');
          resolve('image loaded');
        });
      }
    })

  }

  loadSvgImage( $img ) {
    let src = $img.attr('xlink:href');
    let selectedSrc = this.selectSource( $img );

    return new Promise( (resolve, reject) => {
      if( src === selectedSrc ) {
        resolve( 'image doesn\'t need loading' );
      } else {
        let img = new Image();
        img.onload = () => {
          $img.addClass('lazyloaded');
          resolve( 'svg image loaded' );
        }
        img.src = selectedSrc;
        $img.attr('xlink:href', selectedSrc);
      }
    });
  }

  loadBackgroundImage( $el ) {
    let currentSrc = $el.css('background-image').slice(4, -1).replace(/['"]/g, "");
    let newSrc = this.selectSource( $el );

    return new Promise( (resolve, reject) => {
      if( currentSrc === newSrc ) {
        resolve('image doesn\'t need loading');
      } else {
        $el.css({backgroundImage: `url(${newSrc})`});
        imagesLoaded($el.get(0), {background: true}, () => {
          $el.addClass('lazyloaded');
          resolve('image loaded');
        });
      }
    });
  }

  loadIframe( $el ) {
    return new Promise( (resolve, reject) => {
      if( $el.hasClass('lazyloaded') ) {
        resolve('iframe already is loaded');
      }
      $el.attr('src', $el.data('src')).addClass('lazyloaded');
      resolve('loaded iframe');
    });
  }

  selectSource( $el, width = 'auto' ) {
    let sources = $el.data('sources');
    if( width === 'auto' ) {
      width = this.getScaledImageWidth($el);
    }
    if( isNaN(width) ) {
      this.log("couldn't calculate image size", $el[0]);
      width = 1000;
    }
    let size = 'full';
    $.each(sources, (key, source) => {
      if( source[1] > width ) {
        size = key;
      }
    })
    $el.attr('data-selected-size', size);
    return sources[size][0];
  }


  log( ...args ) {
    if( this.options.debug ) {
      console.log("lazyload:", ...args);
    }
  }

  renderBlurhash() {
    const blurhash = this.$el.data('blurhash');
    const ratio = this.$el.data('ratio');
    const width = 32;
    const height = Math.ceil(32 / ratio);
    if (typeof blurhash !== 'string') return;
    if( isBlurhashValid(blurhash).result === false ) return;
    const pixels = decodeBlurhash(blurhash, width, height);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    const imageData = ctx.createImageData(width, height);
    imageData.data.set(pixels);
    ctx.putImageData(imageData, 0, 0);
    canvas.classList.add('blurhash');
    this.$el.after($(canvas));
  }

}

PluginClass.DEFAULTS = {
  debug: false,
  lazy: true,
  onLoad: () => {}
};

plugin('lazyload', PluginClass);